import React, { useEffect, useState, useRef } from 'react';
import { clsx } from 'clsx';

interface AudioVisualizerProps {
  audioTrack: MediaStreamTrack;
}

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ audioTrack }) => {
  const [volume, setVolume] = useState(0);
  const animationFrameId = useRef<number | null>(null);

  useEffect(() => {
    if (!audioTrack) {
      setVolume(0);
      return;
    }

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    const source = audioContext.createMediaStreamSource(new MediaStream([audioTrack]));
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const animate = () => {
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
      setVolume(average / 128); // Normalize to a 0-1 range

      animationFrameId.current = requestAnimationFrame(animate);
    };

    animationFrameId.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      source.disconnect();
      audioContext.close();
    };
  }, [audioTrack]);

  const isSpeaking = volume > 0.1; // Adjust this speaking threshold if needed

  return (
    <div
      className={clsx(
        "absolute -inset-1 rounded-full border-2 transition-opacity duration-300",
        isSpeaking ? "border-accent-400 animate-pulse" : "border-transparent opacity-0"
      )}
    />
  );
};